import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  List,
  ListItemText,
  Switch,
  Typography,
  Tooltip,
  Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { accessProfileModulePermission, listAdminModules, listAdminModulesActions } from "../../../Requests";
import { IActions, IModules } from "./types";
import ListActions from "./ListActions";
import useGlobalContext from "../../../Hooks/useGlobalContext";
import DynamicIcon from "../../../Components/DynamicIcon";

interface IAccessProfileEditProps {
  idProfile: number;
}

const ManagePermissions: React.FC<IAccessProfileEditProps> = ({ idProfile }) => {
  const { setLoading } = useGlobalContext();

  const [modulesAll, setModulesAll] = useState<IModules[]>([]);
  const [activeModules, setActiveModules] = useState<number[]>([]);
  const [modulesActions, setModulesActions] = useState<IActions[]>([]);
  const [activeActions, setActiveActions] = useState<string[]>([]);

  const [modulesAllLoading, setModulesAllLoading] = useState(true);
  const [activeModulesLoading, setActiveModulesLoading] = useState(true);
  const [modulesActionsLoading, setModulesActionsLoading] = useState(true);
  const [activeActionsLoading, setActiveActionsLoading] = useState(true);

  const [expanded, setExpanded] = useState<string>("");

  const isModuleActive = (moduleId: number) => activeModules.includes(moduleId);

  const fetchModulesAll = async () => {
    const result = await listAdminModules.get();
    setModulesAll(result ?? []);
    setModulesAllLoading(false);
  };

  const fetchModuleActives = async (callBack: (params?: number[]) => void = () => {}) => {
    const result = await listAdminModules.get(`${idProfile}`);
    setActiveModules(result ?? []);
    setActiveModulesLoading(false);
    callBack(result);
  };

  const fetchActionsActive = async (callBack: () => void = () => {}) => {
    const result = await listAdminModulesActions.get(`${idProfile}`);
    setActiveActions(result ?? []);
    setActiveActionsLoading(false);
    callBack();
  };

  const fetchActionsAll = async () => {
    const result = await listAdminModulesActions.get();
    setModulesActions(result ?? []);
    setModulesActionsLoading(false);
  };

  const tryCloseLoading = () => {
    if (
      !modulesAllLoading &&
      !activeModulesLoading &&
      !modulesActionsLoading &&
      !activeActionsLoading
    ) {
      setLoading?.(false);
    }
  };

  const reloadActions = () => fetchActionsActive(() => tryCloseLoading());

  const previewChange = (status: boolean, action: string) => {
    setActiveActions((prev) =>
      status ? [...prev, action] : prev.filter((o) => o !== action)
    );
  };

  const handleSwitchChange = async (status: boolean, idModule: number) => {
    setLoading?.(true);
    if (status) {
      setActiveModules((n) => [...n, idModule]);
    } else {
      setActiveModules((n) => n.filter((o) => o !== idModule));
    }

    const newStatus = status ? "active" : "inactive";
    await accessProfileModulePermission.post(`${idProfile}`, {
      status: newStatus,
      admin_modules_id: idModule,
      admin_modules_access_profile_id: idProfile,
    });

    fetchModuleActives((params) => {
      const statusCurrent = (params ?? []).includes(idModule);
      const success = status === statusCurrent;
      const moduleIdentifier = `module${idModule}`;
      if (success) {
        setExpanded((n) =>
          status || moduleIdentifier === n || n === "" ? (status ? moduleIdentifier : "") : n
        );
      }
      tryCloseLoading();
    });
  };

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : "");
  };

  useEffect(() => {
    setLoading?.(true);
    fetchModulesAll();
    fetchModuleActives();
    fetchActionsActive();
    fetchActionsAll();
  }, []);

  useEffect(() => {
    tryCloseLoading();
  }, [modulesAllLoading, activeModulesLoading, modulesActionsLoading, activeActionsLoading]);

  return (
    <Grid item xs={12}>
      <Box mt={2}>
        {modulesAll.map(({ id = 0, description = "", icon_name = "" }, index) => {
          const isActive = isModuleActive(id);
          const moduleIdentifier = `module${id}`;

          return (
            <Accordion key={index} expanded={expanded === moduleIdentifier} onChange={handleChange(moduleIdentifier)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index + 1}-content`}
                id={`panel${index + 1}-header`}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center">
                    <DynamicIcon iconName={icon_name} marginRight={1} />
                    <Typography fontWeight="bold" fontSize={15}>
                      {description}
                    </Typography>
                  </Box>
                  <Tooltip title={isActive ? "Desativar módulo" : "Ativar módulo"} placement="top">
                    <Switch
                      checked={isActive}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleSwitchChange(!isActive, id);
                      }}
                    />
                  </Tooltip>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {isActive && (
                  <Box mt={1}>
                    <Divider sx={{ mb: 2 }} />
                    <ListActions
                      activeActions={activeActions}
                      actions={modulesActions.filter(({ admin_modules_id }) => admin_modules_id === id)}
                      idModule={id}
                      idProfile={idProfile}
                      reloadActions={reloadActions}
                      previewChange={previewChange}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Grid>
  );
};

export default ManagePermissions;
